import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Settings, Save, Users, GraduationCap, Layers, Briefcase, BookOpen, ToggleLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ConfigTab = ({ institutionId }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/core/institutions/${institutionId}/config`);
      setConfig(data);
      setForm({ name: data.name, email: data.email || '', phone: data.phone || '', website: data.website || '', address: data.address || '' });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [institutionId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/core/institutions/${institutionId}/config`, form);
      toast.success('Configuration sauvegardee');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center p-12"><div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;
  if (!config) return <p className="text-gray-400 text-center py-8">Configuration non disponible</p>;

  const s = config.stats;

  return (
    <div className="space-y-4" data-testid="config-tab">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { icon: Users, label: 'Utilisateurs', value: s.users, color: 'text-blue-600' },
          { icon: GraduationCap, label: 'Etudiants', value: s.students, color: 'text-green-600' },
          { icon: Layers, label: 'Structures', value: s.structures, color: 'text-purple-600' },
          { icon: Briefcase, label: 'Postes', value: s.positions, color: 'text-orange-600' },
          { icon: BookOpen, label: 'Cours', value: s.courses, color: 'text-teal-600' },
          { icon: ToggleLeft, label: 'Modules', value: `${s.modules_active}/${s.modules_total}`, color: 'text-indigo-600' },
          { icon: Users, label: 'Affectations', value: s.assignments, color: 'text-rose-600' },
        ].map(({ icon: Icon, label, value, color }) => (
          <Card key={label}>
            <CardContent className="p-3 text-center">
              <Icon className={`w-5 h-5 mx-auto mb-1 ${color}`} />
              <p className="text-lg font-bold">{value}</p>
              <p className="text-[10px] text-gray-400">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* General Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Settings className="w-4 h-4" /> Informations Generales</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Nom</Label><Input value={form.name || ''} onChange={e => setForm(p => ({...p, name: e.target.value}))} data-testid="config-name" /></div>
            <div><Label>Email</Label><Input value={form.email || ''} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="contact@institution.ac.cd" /></div>
            <div><Label>Telephone</Label><Input value={form.phone || ''} onChange={e => setForm(p => ({...p, phone: e.target.value}))} placeholder="+243..." /></div>
            <div><Label>Site Web</Label><Input value={form.website || ''} onChange={e => setForm(p => ({...p, website: e.target.value}))} /></div>
            <div className="md:col-span-2"><Label>Adresse</Label><Input value={form.address || ''} onChange={e => setForm(p => ({...p, address: e.target.value}))} /></div>
          </div>
          <div className="flex justify-end mt-4">
            <Button onClick={save} disabled={saving} data-testid="save-config-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-medium mb-2">Informations</p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Code</span><Badge variant="outline">{config.code}</Badge></div>
              <div className="flex justify-between"><span className="text-gray-500">Type</span><span>{config.type}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Province</span><span>{config.province || '-'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Ville</span><span>{config.city || '-'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Pays</span><span>{config.country}</span></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-medium mb-2">Statut</p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Actif</span><Badge variant={config.is_active ? 'default' : 'destructive'}>{config.is_active ? 'Oui' : 'Non'}</Badge></div>
              <div className="flex justify-between"><span className="text-gray-500">Cree le</span><span>{new Date(config.created_at).toLocaleDateString('fr-FR')}</span></div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
